import {
  ComponentBatchIngredient,
  CreateComponentBatchIngredientRequest,
  UpdateComponentBatchIngredientRequest
} from '../types/componentBatchIngredient';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

export const componentBatchIngredientApiService = {
  /**
   * Fetch all component batch ingredients from the backend
   */
  fetchAllComponentBatchIngredients: async (): Promise<ComponentBatchIngredient[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/component-batch-ingredients`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch component batch ingredients');
      }

      return data.componentBatchIngredients;
    } catch (error) {
      console.error('Error fetching component batch ingredients from API:', error);
      throw error;
    }
  },

  /**
   * Fetch component batch ingredients by component batch ID
   */
  fetchComponentBatchIngredientsByComponentBatchId: async (componentBatchId: string): Promise<ComponentBatchIngredient[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/component-batch-ingredients/by-component-batch/${componentBatchId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch component batch ingredients');
      }

      return data.componentBatchIngredients;
    } catch (error) {
      console.error('Error fetching component batch ingredients by component batch ID from API:', error);
      throw error;
    }
  },

  /**
   * Create a new component batch ingredient
   */
  createComponentBatchIngredient: async (ingredient: CreateComponentBatchIngredientRequest): Promise<ComponentBatchIngredient> => {
    try {
      const response = await fetch(`${API_BASE_URL}/component-batch-ingredients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ingredient)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create component batch ingredient');
      }

      return data.componentBatchIngredient;
    } catch (error) {
      console.error('Error creating component batch ingredient:', error);
      throw error;
    }
  },

  /**
   * Update an existing component batch ingredient
   */
  updateComponentBatchIngredient: async (id: string, ingredient: UpdateComponentBatchIngredientRequest): Promise<ComponentBatchIngredient> => {
    try {
      const response = await fetch(`${API_BASE_URL}/component-batch-ingredients/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ingredient)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update component batch ingredient');
      }

      return data.componentBatchIngredient;
    } catch (error) {
      console.error('Error updating component batch ingredient:', error);
      throw error;
    }
  },

  /**
   * Delete a component batch ingredient by ID
   */
  deleteComponentBatchIngredient: async (id: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/component-batch-ingredients/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete component batch ingredient');
      }
    } catch (error) {
      console.error('Error deleting component batch ingredient:', error);
      throw error;
    }
  }
};
